import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../component/Header';
import Footer from '../component/footer';
import SignupButton from '../component/sign-up'; // Import your component
import { Check } from 'lucide-react';
import { getAuthChangeEventName, getAuthToken, isAuthenticated } from '../utils/authSession';
import { purchaseSubscription } from '../services/subscriptionService';
import { showSuccessToast, showErrorToast } from '../utils/toaster';

const Pricing = () => {
  const navigate = useNavigate();
  const [loggedIn, setLoggedIn] = useState(isAuthenticated());
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    const refreshAuth = () => setLoggedIn(isAuthenticated());
    const authEventName = getAuthChangeEventName();

    window.addEventListener('storage', refreshAuth);
    window.addEventListener(authEventName, refreshAuth);

    return () => {
      window.removeEventListener('storage', refreshAuth);
      window.removeEventListener(authEventName, refreshAuth);
    };
  }, []);

  const handlePurchase = async (planName: string, price: number) => {
    if (!loggedIn) {
      navigate('/login?mode=signup');
      return;
    }

    const token = getAuthToken();
    if (!token) {
      showErrorToast('Please login to continue');
      navigate('/login');
      return;
    }

    // setIsProcessing(true);
    // try {
    //   await purchaseSubscription(token, {
    //     plan_name: planName,
    //     price: price,
    //   });
    //   showSuccessToast('Payment Successfully');
    //   // Optionally redirect to my-account or library
    //   setTimeout(() => navigate('/my-account'), 1500);
    // } catch (error: any) {
    //   showErrorToast(error?.message || 'Payment failed. Please try again.');
    // } finally {
    //   setIsProcessing(false);
    // }
  };

  // Data strictly extracted from the provided image
  const plans = [
    {
      name: 'Basic',
      price: '$29',
      priceValue: 29,
      period: '/Per Month',
      features: [
        'Access to Basic Courses',
        'Access on any device',
        'Cancel Anytime',
      ],
      popular: false,
      buttonText: 'Get Started',
    },
    {
      name: 'Premium',
      price: '€99',
      priceValue: 99,
      period: '/Per Month',
      features: [
        'Access to All Courses',
        'Access On Any Device',
        'Unlimited Downloads',
        'Unlimited Transcripts',
        'Cancel Anytime',
      ],
      popular: true, // Highlighted center plan
      buttonText: 'Sign up now',
    },
    {
      name: 'Standard',
      price: '€59',
      priceValue: 59,
      period: '/Per Month',
      features: [
        'Access to Advanced Courses',
        'Access on any device',
        'Limited Downloads',
        'Limited Transcripts',
        'Cancel Anytime',
      ],
      popular: false,
      buttonText: 'Sign up now',
    },
  ];

  return (
    <>
      <Header />
      
    <div className="min-h-screen flex flex-col bg-[#F4F5F7]">
      <main className="flex-grow pt-32 pb-24 px-6 md:px-12">
        {/* Page Titles */}
        <div className="max-w-7xl mx-auto text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-[#113A28] tracking-tight mb-4">
            Simple, transparent pricing
          </h1>
          <p className="text-gray-500 text-lg max-w-2xl mx-auto">
            Choose the plan that best fits your learning goals. Upgrade, downgrade, or cancel anytime.
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-6 lg:gap-8 items-center">
          {plans.map((plan, index) => (
            <div
              key={index}
              className={`relative rounded-[2.5rem] overflow-hidden transition-all duration-500 hover:shadow-2xl flex flex-col ${
                plan.popular
                  ? 'bg-[#113A28] text-white shadow-xl md:-translate-y-4 md:scale-105 z-10 border-4 border-[#4CE470]/20'
                  : 'bg-white text-[#113A28] shadow-md border border-gray-100 hover:-translate-y-2'
              }`}
            >
              {/* Plan Header Strip */}
              <div className={`py-6 text-center border-b ${
                plan.popular ? 'border-white/10 bg-white/5' : 'border-gray-100 bg-gray-50'
              }`}>
                <h3 className={`text-2xl font-bold tracking-wide ${plan.popular ? 'text-[#4CE470]' : 'text-[#113A28]'}`}>
                  {plan.name}
                </h3>
              </div>
              
              <div className="p-8 md:p-10 flex flex-col flex-grow">
                {/* Price Section */}
                <div className="text-center mb-10">
                  <span className="text-5xl font-bold">{plan.price}</span>
                  <span className={`text-sm ${plan.popular ? 'text-gray-300' : 'text-gray-500'}`}>
                    {plan.period}
                  </span>
                </div>
                
                {/* Use the component here */}
                <div className="space-y-4 mt-8">
                  {plan.features.map((feature, i) => (
                    <div key={i} className="flex items-center gap-4">
                      {/* Big stylized checkmarks like the image */}
                      <Check
                        size={28}
                        strokeWidth={3}
                        className={`flex-shrink-0 ${
                          plan.popular ? 'text-[#4CE470]' : 'text-[#113A28]'
                        }`}
                      />
                      <span
                        className={`text-base font-medium ${
                          plan.popular ? 'text-gray-200' : 'text-gray-600'
                        }`}
                      >
                        
                        {feature}
                      </span>
                    </div>
                  ))}
                  <SignupButton 
                    to={loggedIn ? '' : '/login?mode=signup'}
                    text={loggedIn ? (isProcessing ? 'PROCESSING...' : 'GET STARTED') : 'SIGN UP'}
                    className="w-full" 
                    onClick={loggedIn ? () => handlePurchase(plan.name, plan.priceValue) : null}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

      </main>


    </div>
          <Footer />
    </>
  );
};

export default Pricing;
